/**
 * ChatLayout Component - HYB Shop Chat
 * Responsive two-panel layout with elegant transitions
 *
 * Design: Warm professional aesthetic with smooth panel animations
 * - Desktop: Fixed sidebar (320px) + flexible chat area
 * - Mobile: Full-width panels with slide transitions
 */


import { cn } from '../../../utils/responsive';

interface ChatLayoutProps {
  /** Array of two children: [ConversationList, ChatWindow] */
  children: [React.ReactNode, React.ReactNode];
  /** Whether to show chat panel on mobile (hides list) */
  showChatOnMobile?: boolean;
  /** Callback when panel visibility changes */
  onPanelChange?: (showChat: boolean) => void;
  /** Additional CSS classes for the container */
  className?: string;
}

export function ChatLayout({
  children,
  showChatOnMobile = false,
  className,
}: ChatLayoutProps) {
  const [listPanel, chatPanel] = children;

  return (
    <div
      className={cn(
        // Base grid layout
        'grid gap-4 lg:gap-6',
        // Desktop: two columns
        'lg:grid-cols-[320px,1fr]',
        // Height calculation using dvh for better mobile support
        'h-[calc(100dvh-200px)] lg:h-[calc(100dvh-220px)]',
        // Smooth transitions
        'transition-all duration-300 ease-spring',
        className
      )}
    >
      {/* Conversation List Panel */}
      <div
        className={cn(
          // Base styles
          'relative overflow-hidden',
          'rounded-2xl',
          'bg-white dark:bg-gray-800',
          'shadow-sm shadow-stone-200/50 dark:shadow-none',
          'ring-1 ring-stone-100 dark:ring-gray-700/60',
          // Transition
          'transition-all duration-300 ease-spring',
          // Mobile: hide when showing chat
          showChatOnMobile && 'hidden lg:block',
          // Desktop: always visible
          !showChatOnMobile && 'block'
        )}
      >
        {/* Subtle gradient overlay at top */}
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 top-0 z-10 h-1',
            'bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400',
            'opacity-80'
          )}
          aria-hidden="true"
        />
        {listPanel}
      </div>

      {/* Chat Window Panel */}
      <div
        className={cn(
          // Base styles
          'relative overflow-hidden',
          'rounded-2xl',
          'bg-white dark:bg-gray-800',
          'shadow-sm shadow-stone-200/50 dark:shadow-none',
          'ring-1 ring-stone-100 dark:ring-gray-700/60',
          // Animation
          'transition-all duration-300 ease-spring',
          // Mobile: show with slide animation
          !showChatOnMobile && 'hidden lg:block',
          showChatOnMobile && 'block animate-slide-in-right lg:animate-none'
        )}
      >
        {/* Subtle gradient overlay at top */}
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 top-0 z-10 h-1',
            'bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400',
            'opacity-80'
          )}
          aria-hidden="true"
        />
        {chatPanel}
      </div>
    </div>
  );
}

export default ChatLayout;
